<template>
    <div
        class="warnings-table-page text-start"
        data-aos="fade-left"
        data-aos-duration="1500"
    >
        <v-container class="p-0" fluid>
            <div class="page-card">
                <div class="d-flex justify-content-between align-items-start">
                    <div>
                        <h1 class="card-title mb-2">Avisos</h1>
                        <p class="card-subtitle mb-4 mb-md-0">
                            Gerencie os avisos e notificações do sistema.
                        </p>
                    </div>
                    <button
                        class="btnd btnd--purple btnd--prepend-icon text-capitalize pr-4 pr-md-6"
                        @click="openNewWarningDialog"
                        title="Criar novo aviso"
                    >
                        <Icon name="plus" size="20" class="mr-0 mr-md-2" />
                        <span class="d-none d-md-block">Novo aviso</span>
                    </button>
                </div>
                <hr class="page-card__divider" />

                <v-row class="mt-1 mb-2" justify-sm="space-between">
                    <v-col cols="12" sm="8" md="6" lg="8">
                        <v-text-field
                            v-model="searchText"
                            label="Pesquisar aviso"
                            class="inputd"
                            variant="outlined"
                            density="compact"
                            hide-details
                            @update:model-value="onSearch"
                        >
                            <template v-slot:append-inner>
                                <Icon name="magnifyingGlass" size="20" />
                            </template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="4" md="4" lg="4">
                        <v-select
                            v-model="statusFilter"
                            :items="statusFilterOptions"
                            item-title="text"
                            item-value="value"
                            label="Status"
                            variant="outlined"
                            class="inputd"
                            @update:modelValue="filtrarAvisos"
                            density="compact"
                            hide-details
                        />
                    </v-col>
                </v-row>

                <div data-aos="fade-up" data-aos-duration="1500">
                    <v-table class="custom-table">
                        <thead>
                            <tr>
                                <th class="warnings-table__title-column">
                                    Título
                                </th>
                                <th class="warnings-table__description-column">
                                    Descrição
                                </th>
                                <th class="text-center">Status</th>
                                <th class="text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="warning in paginatedWarnings"
                                :key="warning.id"
                            >
                                <td class="warnings-table__title-column">
                                    {{ warning.title }}
                                </td>
                                <td class="warnings-table__description-column">
                                    {{
                                        truncateDescription(warning.description)
                                    }}
                                </td>
                                <td class="text-center">
                                    <v-chip
                                        density="compact"
                                        :color="getStatusColor(warning.status)"
                                    >
                                        {{ getStatusText(warning.status) }}
                                    </v-chip>
                                </td>
                                <td class="text-center">
                                    <div class="d-flex justify-center gap-1">
                                        <v-btn
                                            color="heading"
                                            @click="
                                                openEditWarningDialog(warning)
                                            "
                                            icon
                                            variant="text"
                                            title="Editar aviso"
                                        >
                                            <Icon name="pencil" size="20" />
                                        </v-btn>
                                        <v-btn
                                            color="danger"
                                            @click="confirmDelete(warning)"
                                            icon
                                            variant="text"
                                            title="Excluir aviso"
                                        >
                                            <Icon name="trash" size="20" />
                                        </v-btn>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </div>

                <v-responsive>
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        :total-visible="5"
                        :disabled="!hasReady"
                        @update:model-value="onPageChange"
                    ></v-pagination>
                </v-responsive>

                <Loader v-if="!hasReady" />
            </div>
        </v-container>

        <!-- Create New Warning Dialog -->
        <v-dialog v-model="newWarningDialog" max-width="600">
            <v-card class="modal-card">
                <v-card-title>
                    <h3 class="mb-0">Criar Novo Aviso</h3>
                </v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="newWarning.title"
                        label="Título do Aviso"
                        required
                        variant="outlined"
                        hide-details
                        density="compact"
                        class="mb-4"
                    />

                    <v-textarea
                        v-model="newWarning.description"
                        label="Descrição do Aviso"
                        required
                        auto-grow
                        rows="4"
                        variant="outlined"
                        hide-details
                        density="compact"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        class="flex-grow-1 cancel-btn"
                        color="grey-darken-4"
                        variant="tonal"
                        @click="closeNewWarningDialog"
                    >
                        Cancelar
                    </v-btn>
                    <v-btn
                        class="flex-grow-1 text-white"
                        color="primary"
                        variant="flat"
                        :loading="isCreatingWarning"
                        :disabled="
                            !newWarning.title.trim() || isCreatingWarning
                        "
                        @click="createNewWarning"
                    >
                        Publicar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Edit Warning Dialog -->
        <v-dialog v-model="editWarningDialog" max-width="600">
            <v-card class="modal-card">
                <v-card-title>
                    <h3 class="mb-0">Editar Aviso</h3>
                </v-card-title>
                <v-card-text>
                    <div class="mb-4">
                        <v-switch
                            v-model="editingWarning.status"
                            :label="getStatusText(editingWarning.status)"
                            :true-value="1"
                            :false-value="0"
                            color="primary"
                            hide-details
                        />
                    </div>

                    <v-text-field
                        v-model="editingWarning.title"
                        label="Título do Aviso"
                        required
                        variant="outlined"
                        hide-details
                        density="compact"
                        class="mb-4"
                    />

                    <v-textarea
                        v-model="editingWarning.description"
                        label="Descrição do Aviso"
                        required
                        auto-grow
                        rows="4"
                        variant="outlined"
                        hide-details
                        density="compact"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        class="flex-grow-1 cancel-btn"
                        color="grey-darken-4"
                        variant="tonal"
                        @click="closeEditWarningDialog"
                    >
                        Cancelar
                    </v-btn>
                    <v-btn
                        class="flex-grow-1 text-white"
                        color="primary"
                        variant="flat"
                        :loading="isUpdatingWarning"
                        :disabled="
                            !editingWarning.title.trim() || isUpdatingWarning
                        "
                        @click="updateWarning"
                    >
                        Salvar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="deleteDialog" max-width="480">
            <v-card class="modal-card">
                <v-card-title>
                    <h3 class="mb-0">Excluir Aviso</h3>
                </v-card-title>
                <v-card-subtitle>
                    Esta ação não poderá ser desfeita.
                </v-card-subtitle>
                <v-card-text>
                    Tem certeza que deseja excluir o aviso
                    <strong>"{{ warningToDelete?.title }}"</strong>?
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        class="flex-grow-1 cancel-btn"
                        color="grey-darken-4"
                        variant="tonal"
                        @click="deleteDialog = false"
                    >
                        Cancelar
                    </v-btn>
                    <v-btn
                        class="flex-grow-1 text-white"
                        color="error"
                        variant="flat"
                        :loading="isDeletingWarning"
                        :disabled="isDeletingWarning"
                        @click="confirmDeleteWarning"
                    >
                        Excluir
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import Loader from '@/components/Loader/loader.vue';
    import api from '@/utils/notification';
    import { debounce } from 'lodash';
    import { mapState } from 'vuex';
    import { toast } from 'vue3-toastify';

    export default {
        components: {
            Loader,
        },
        data() {
            return {
                notificationsData: [],
                searchText: '',
                currentPage: 1,
                itemsPerPage: 10,
                hasReady: false,
                isCreatingWarning: false,
                isUpdatingWarning: false,
                isDeletingWarning: false,
                statusFilter: 'todos',
                statusFilterOptions: [
                    { text: 'Todos', value: 'todos' },
                    { text: 'Ativo', value: 1 },
                    { text: 'Inativo', value: 0 },
                ],
                newWarningDialog: false,
                editWarningDialog: false,
                deleteDialog: false,
                warningToDelete: null,
                newWarning: {
                    title: '',
                    description: '',
                    status: 1,
                },
                editingWarning: {
                    id: null,
                    title: '',
                    description: '',
                    status: 1,
                },
            };
        },
        computed: {
            ...mapState(['token', 'numberIntensTable']),

            paginatedWarnings() {
                const startIndex = (this.currentPage - 1) * this.itemsPerPage;
                const endIndex = startIndex + this.itemsPerPage;
                return this.filteredWarnings.slice(startIndex, endIndex);
            },

            totalPages() {
                return Math.ceil(
                    this.filteredWarnings.length / this.itemsPerPage
                );
            },

            filteredWarnings() {
                let filtered = this.notificationsData;

                if (this.searchText && this.searchText.trim() !== '') {
                    filtered = filtered.filter(
                        (warning) =>
                            warning.title
                                .toLowerCase()
                                .includes(this.searchText.toLowerCase()) ||
                            warning.description
                                .toLowerCase()
                                .includes(this.searchText.toLowerCase())
                    );
                }

                if (this.statusFilter !== 'todos') {
                    filtered = filtered.filter(
                        (warning) =>
                            String(warning.status) === String(this.statusFilter)
                    );
                }

                return filtered;
            },
        },
        created() {
            this.getDataNotification();
        },
        methods: {
            getStatusColor(status) {
                return status === 1 ? 'success' : 'error';
            },

            getStatusText(status) {
                return status === 1 ? 'Ativo' : 'Inativo';
            },

            truncateDescription(description, maxLength = 100) {
                if (!description) return '';
                return description.length > maxLength
                    ? description.substring(0, maxLength) + '...'
                    : description;
            },

            openNewWarningDialog() {
                this.newWarningDialog = true;
            },

            closeNewWarningDialog() {
                this.newWarningDialog = false;
                this.newWarning = {
                    title: '',
                    description: '',
                    status: 1,
                };
            },

            openEditWarningDialog(warning) {
                this.editingWarning = {
                    id: warning.id,
                    title: warning.title,
                    description: warning.description,
                    status: warning.status,
                };
                this.editWarningDialog = true;
            },

            closeEditWarningDialog() {
                this.editWarningDialog = false;
                this.editingWarning = {
                    id: null,
                    title: '',
                    description: '',
                    status: 1,
                };
            },

            confirmDelete(warning) {
                this.warningToDelete = warning;
                this.deleteDialog = true;
            },

            async confirmDeleteWarning() {
                if (this.warningToDelete) {
                    this.isDeletingWarning = true;
                    try {
                        await this.deleteWarning(this.warningToDelete.id);
                        this.deleteDialog = false;
                        this.warningToDelete = null;
                    } catch (error) {
                        console.error('Error deleting warning:', error);
                    } finally {
                        this.isDeletingWarning = false;
                    }
                }
            },

            async createNewWarning() {
                if (!this.newWarning.title.trim()) {
                    toast.error('Título do aviso é obrigatório');
                    return;
                }

                this.isCreatingWarning = true;
                const payload = {
                    title: this.newWarning.title.trim(),
                    description: this.newWarning.description.trim(),
                    status: 1,
                };

                try {
                    await api.createNewNotification(payload, this.token);
                    await this.getDataNotification();
                    toast.success('Aviso criado com sucesso!');
                    this.closeNewWarningDialog();
                } catch (error) {
                    toast.error('Erro ao criar aviso!');
                    console.error('Erro ao criar aviso:', error);
                } finally {
                    this.isCreatingWarning = false;
                }
            },

            async updateWarning() {
                if (!this.editingWarning.title.trim()) {
                    toast.error('Título do aviso é obrigatório');
                    return;
                }

                this.isUpdatingWarning = true;
                const payload = {
                    id: this.editingWarning.id,
                    title: this.editingWarning.title.trim(),
                    description: this.editingWarning.description.trim(),
                    status: this.editingWarning.status,
                };

                try {
                    await api.editNotification(
                        this.editingWarning.id,
                        this.token,
                        payload
                    );
                    await this.getDataNotification();
                    toast.success('Aviso atualizado com sucesso!');
                    this.closeEditWarningDialog();
                } catch (error) {
                    toast.error('Erro ao atualizar aviso!');
                    console.error('Erro ao atualizar aviso:', error);
                } finally {
                    this.isUpdatingWarning = false;
                }
            },

            async deleteWarning(id) {
                try {
                    const payload = {
                        id: id,
                        status: 0,
                    };
                    await api.editNotification(id, this.token, payload);
                    await this.getDataNotification();
                    toast.success('Aviso deletado com sucesso!');
                } catch (error) {
                    toast.error('Erro ao deletar aviso!');
                    console.error('Erro ao deletar aviso:', error);
                    throw error;
                }
            },

            async getDataNotification() {
                try {
                    this.hasReady = false;
                    const response = await api.getDataNotification(this.token);
                    this.notificationsData = response;
                } catch (error) {
                    toast.error('Erro ao carregar notificações!');
                    console.error('Erro ao carregar notificações', error);
                } finally {
                    this.hasReady = true;
                }
            },

            filtrarAvisos() {
                this.currentPage = 1;
            },

            onSearch: debounce(function () {
                this.currentPage = 1;
            }, 300),

            onPageChange() {},
        },
    };
</script>

<style scoped>
    .warnings-table__title-column {
        width: 30%;
    }

    .warnings-table__description-column {
        width: 40%;
    }

    .custom-table {
        margin-bottom: 1rem;
    }

    .gap-1 {
        gap: 0.25rem;
    }
    .search-form-input {
        position: relative;
        width: 100%;
    }
    .title-container {
        position: relative;
        margin-bottom: 10px;
        padding-bottom: 0;
        border-bottom: 1px solid #f0f0f0;
        text-align: left;
    }

    .title-container .title {
        font-size: 1.5rem;
        font-weight: 700;
        color: #000000;
        margin-bottom: 0.5rem;
    }

    .title-container .subtitle {
        color: #6b7280;
        font-size: 0.875rem;
        line-height: 1.4;
        margin-bottom: 1rem;
    }

    .search-row {
        display: flex;
        gap: 0.75rem;
        margin-bottom: 1.5rem;
    }

    .search-row .form-group {
        flex: 1;
    }

    .btn-weflix.absolute {
        position: absolute;
        top: 0;
        right: 0;
        background-color: #2f2e8b;
        color: white;
        padding: 0.5rem 1rem;
        border-radius: 6px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-weight: 600;
        transition: all 0.3s ease;
        box-shadow: 0 2px 4px rgba(47, 46, 139, 0.2);
    }

    .btn-weflix.absolute:hover {
        background-color: #23225f;
        transform: translateY(-2px);
    }

    .warnings-table-responsive {
        background-color: #ffffff;
        border-radius: 8px;
        overflow: hidden;
        margin-bottom: 1rem;
    }

    .warnings-table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
    }

    .weeducAdminWarnings .warnings-table th {
        background-color: #f8fafc;
        padding: 0.75rem;
        font-weight: 600;
        color: #4a5568;
        text-align: left;
        border-bottom: 2px solid #e2e8f0;
    }

    .weeducAdminWarnings .warnings-table td {
        padding: 0.75rem;
        vertical-align: middle;
        border-bottom: 1px solid #f0f0f0;
        color: #4a5568;
        text-align: left;
    }

    .weeducAdminWarnings .warnings-table tr:hover {
        background-color: #f8fafc;
    }

    .weeducAdminWarnings .warnings-table .col-id {
        min-width: 160px;
        width: 16%;
        max-width: 180px;
    }

    .weeducAdminWarnings .warnings-table .col-title {
        min-width: 240px;
        width: 24%;
        max-width: 300px;
    }

    .weeducAdminWarnings .warnings-table .col-description {
        min-width: 500px;
        width: 50%;
    }

    .weeducAdminWarnings .warnings-table .col-actions {
        min-width: 100px;
        width: 10%;
        max-width: 120px;
    }

    .badge-active,
    .badge-inactive {
        padding: 0.25rem 0.75rem;
        border-radius: 5px;
        font-size: 0.75rem;
        font-weight: 500;
        display: inline-block;
        text-align: center;
        transition: all 0.2s ease;
        width: 70%;
    }

    .badge-active {
        background-color: #7bcc8180;
        color: #4b614e;
    }

    .badge-inactive {
        background-color: #d7d7ee80;
        color: #2f2e8b;
    }

    .warning-btn-edit,
    .warning-btn-delete {
        padding: 0.4rem;
        margin: 0 0.25rem;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s ease;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
    }

    .warning-btn-edit {
        color: #2f2e8b;
    }

    .warning-btn-delete {
        color: #dc2626;
    }

    .warning-btn-edit:hover {
        background-color: #e4e3ff;
        transform: translateY(-2px);
    }

    .warning-btn-delete:hover {
        background-color: #fee2e2;
        transform: translateY(-2px);
    }

    .search-form-input {
        position: relative;
    }

    .search-form-control {
        width: 100%;
        padding: 0.5rem 0.75rem;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        font-size: 0.875rem;
        transition: all 0.2s ease;
    }

    .search-form-control:focus {
        outline: none;
        border-color: #2f2e8b;
        box-shadow: 0 0 0 3px rgba(47, 46, 139, 0.1);
    }

    .search-input-icon {
        position: absolute;
        right: 0.75rem;
        top: 45%;
        transform: translateY(-50%);
        color: #9ca3af;
        cursor: pointer;
    }

    .warnings-pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 1rem;
        margin-top: 1rem;
    }

    .warnings-page-btn {
        padding: 0.5rem 1rem;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        background: white;
        color: #4a5568;
        cursor: pointer;
        transition: all 0.2s;
    }

    .warnings-page-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .warnings-page-btn:not(:disabled):hover {
        background: #f8fafc;
        border-color: #cbd5e0;
    }

    .warnings-page-info {
        color: #4a5568;
        font-size: 0.875rem;
    }

    @media (max-width: 768px) {
        .title-container {
            margin-bottom: 1.5rem;
        }

        .search-row {
            flex-direction: column;
        }

        .weeducAdminWarnings .warnings-table th,
        .weeducAdminWarnings .warnings-table td {
            padding: 0.5rem;
            font-size: 14px;
        }

        .weeducAdminWarnings .warnings-table .col-id {
            min-width: 120px;
        }

        .weeducAdminWarnings .warnings-table .col-title {
            min-width: 200px;
        }

        .weeducAdminWarnings .warnings-table .col-description {
            min-width: 400px;
        }

        .weeducAdminWarnings .warnings-table .col-actions {
            min-width: 80px;
        }
    }

    @media (max-width: 600px) {
        .btn-weflix.absolute {
            padding: 0.5rem;
        }

        .btn-weflix.absolute span {
            display: none;
        }

        .warnings-table-responsive {
            overflow-x: auto;
        }
    }

    select.form-control.filter-form-control {
        width: 65%;
    }

    button.btn-edit.warning-btn-edit svg {
        color: #2f2e8b;
    }
    button.btn-delete.warning-btn-delete svg {
        color: #dc2626;
    }

    .form-group.filter-form-group {
        display: flex;
    }
    .btn-weflix.absolute span.flex {
        font-size: 16px;
    }
    .btn-weflix.absolute svg {
        color: white;
        font-size: 15px;
    }

    /* Estilos melhorados para os diálogos */
    .dialog-card {
        border-radius: 12px;
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
        background: #ffffff;
        overflow: hidden;
    }

    .dialog-title {
        font-size: 1.5rem;
        font-weight: 600;
        color: #2f2e8b;
        padding: 1.5rem;
        border-bottom: 1px solid #e2e8f0;
        background: #f8fafc;
    }

    .dialog-title.delete {
        color: #dc2626;
        background: #fee2e2;
    }

    .dialog-content {
        padding: 1.5rem;
    }

    .dialog-input,
    .dialog-select,
    .dialog-textarea {
        margin-bottom: 1.25rem;
    }

    .dialog-input :deep(input),
    .dialog-select :deep(select),
    .dialog-textarea :deep(textarea) {
        border-radius: 8px;
        font-size: 0.875rem;
        width: 100%;
        transition: all 0.2s ease;
    }

    .dialog-input :deep(input:focus),
    .dialog-select :deep(select:focus),
    .dialog-textarea :deep(textarea:focus) {
        border-color: #2f2e8b;
        box-shadow: 0 0 0 3px rgba(47, 46, 139, 0.1);
        mask-image: linear-gradient(
            to bottom,
            transparent,
            transparent
                calc(
                    var(--v-field-padding-top, 0) +
                        var(--v-input-padding-top, 0) - 6px
                ),
            black
                calc(
                    var(--v-field-padding-top, 0) +
                        var(--v-input-padding-top, 0) + 4px
                )
        );
    }

    .dialog-textarea :deep(textarea) {
        min-height: 150px;
        resize: vertical;
    }

    .dialog-textarea .v-field__input {
        padding-top: 28px !important;
        background: transparent !important;
    }

    .dark .dialog-textarea textarea {
        background-color: #1a1a1a !important;
        color: #ffffff;
        border-color: #444444;
    }

    .dark .dialog-textarea textarea:focus {
        border-color: #2f2e8b;
    }

    .dialog-actions {
        padding: 1rem 1.5rem;

        border-top: 1px solid #e2e8f0;
        display: flex;
        justify-content: flex-end;
        gap: 1rem;
    }

    .dialog-btn {
        padding: 0.5rem 1.5rem;
        border-radius: 6px;
        font-weight: 500;
        text-transform: none;
        transition: all 0.2s ease;
    }

    .dialog-btn.cancel {
        color: #64748b;
        background: #f1f5f9;
    }

    .dialog-btn.confirm {
        color: #ffffff;
        background: #2f2e8b;
    }

    .dialog-btn.delete {
        color: #ffffff !important;
        background: #dc2626 !important;
    }

    .dialog-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .dialog-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    /* Animações para os diálogos */
    .v-dialog-transition-enter-active,
    .v-dialog-transition-leave-active {
        transition: all 0.3s ease;
    }

    .v-dialog-transition-enter-from,
    .v-dialog-transition-leave-to {
        opacity: 0;
        transform: scale(0.95);
    }

    /* Estilo para o status switch */
    .status-switch {
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .status-switch label {
        font-size: 0.875rem;
        color: #4a5568;
        font-weight: 500;
    }

    .dark .status-switch label {
        color: #fff;
    }

    .dialog-switch {
        margin: 0;
    }

    .new-warning-dialog .v-overlay__content,
    .edit-warning-dialog .v-overlay__content {
        width: auto !important;
        max-width: calc(100% - 48px) !important;
    }

    .new-warning-dialog .modal-header,
    .edit-warning-dialog .modal-header {
        padding: 16px 24px 10px;
    }

    .new-warning-dialog__card,
    .edit-warning-dialog__card {
        min-width: clamp(300px, 50vw, 600px);
    }

    .dark .new-warning-dialog__card,
    .dark .edit-warning-dialog__card {
        background-color: #1a1a1a;
        color: #ffffff;
    }

    .v-input.v-input--horizontal.v-input--center-affix.v-input--density-default.v-locale--is-ltr.v-input--dirty.v-switch.dialog-switch
        .v-input__details {
        display: none !important;
    }
    textarea#input-23 {
        padding-top: 20px !important;
    }
</style>
